import { LinkIcon } from 'assets';
import { Container, ControlledInput } from 'components';
import { SEO } from 'components/SEO';
import { ProductAssetTypes } from 'consts';
import { PageProps } from 'gatsby';
import { FormattedMessage, useIntl } from 'gatsby-plugin-intl';
import { Product } from 'models';
import { getCartModel } from 'modules/calculator';
import { CartProduct } from 'modules/cart';
import { CartThunks } from 'modules/cart/redux';
import { SuccessToast } from 'modules/inbox';
import { Breadcrumbs } from 'modules/navigation';
import {
  Guarantee,
  LastViewedProducts,
  MobileDetailsAccordion,
  ProductColorAlternatives,
  ProductImage,
  ProductsThunks,
  SystemSolutions,
  TechnicalDetails,
  VideoMaterials,
} from 'modules/products';
import { AppDispatch } from 'modules/redux-store';
import React, { lazy, Suspense, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast, ToastContainer } from 'react-toastify';
import { product, spacers } from 'styles';
import {
  createMarkup,
  formatPrice,
  formatPriceInEuros,
  localizeNavCategory,
} from 'utils';

const RoofCalculators = lazy(
  () => import('modules/products/components/RoofCalculators'),
);
const WallCalculators = lazy(
  () => import('modules/products/components/WallCalculators'),
);

const ProductPage: React.FC<PageProps<unknown, Product>> = ({
  pageContext,
}) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch<AppDispatch>();
  const [quantity, setQuantity] = useState<number>(1);
  const delivery = 'construction-site';
  const [quantityError, setQuantityError] = useState<boolean>(false);

  const { commercial, sku, skuAccessories, colorsObject, dryfixQuery } =
    pageContext;

  const minPiecesPerMsq =
    sku?.salesRelation.piecesMax ||
    sku?.salesRelation.piecesDry ||
    sku?.salesRelation.piecesMortar;

  const productPricePerM2 = (minPiecesPerMsq || 0) * sku?.price?.pricePerPiece;
  const totalPrice = (quantity || 0) * sku?.price?.pricePerPiece;

  useEffect(() => {
    return () => {
      dispatch(ProductsThunks.updateLastViewedProducts(pageContext));
    };
  }, [dispatch, pageContext]);

  const productImage = commercial.assets.mainImg;

  const promoImages = commercial.assets.otherImgs
    .filter((imgs) => imgs.assetType === ProductAssetTypes.Promo)
    .map((img) => img.imgUrl);

  const isRoof = commercial.navigationCategories.code === 'Roof';
  const isWall = commercial.navigationCategories.code === 'Wall';
  const isRoofAccessories = commercial.navigationCategories.code === 'RoofAcc';
  const isRoofWindow = commercial.navigationCategories.code === 'RoofWindows';

  const brickNameForCalculator = isWall
    ? commercial.name.replace(/ /g, '').toLowerCase()
    : '';
  const roofModelNameForCalculator = isRoof
    ? commercial.model.replace(/ /g, '').toLowerCase()
    : '';

  function getCategorySlug() {
    if (commercial.navigationCategories.code === 'Facade') return 'Terca';
    if (
      commercial.navigationCategories.code === 'RoofWindows' ||
      isRoofAccessories
    )
      return 'Tondach';
    if (commercial.category) return `${commercial.category} line`;
    return 'Sistemska rješenja';
  }

  return (
    <Container>
      <SEO
        productName={getSEOTitle() + commercial.localizedName}
        productDescription={commercial.description}
      />
      <Breadcrumbs
        navigationCategory={localizeNavCategory(
          commercial.navigationCategories.code,
        )}
        category={getCategorySlug()}
        model={commercial.model}
        product={commercial.localizedName}
      />

      <div css={product.layout}>
        <section css={[product.layoutFlexItem, product.leftSection]}>
          <ProductImage
            productImage={productImage}
            className={product.desktopImageWrapper}
            images={[productImage, ...promoImages]}
          />

          {isRoof && (
            <Suspense fallback={null}>
              <RoofCalculators
                roofModelName={roofModelNameForCalculator}
                className={product.desktopCalc}
              />
            </Suspense>
          )}

          {isWall && (
            <Suspense fallback={null}>
              <WallCalculators
                brickName={brickNameForCalculator}
                className={product.desktopCalc}
              />
            </Suspense>
          )}

          {!isRoofAccessories && (
            <div css={[product.calculators, product.desktopCalc]}>
              <h4 css={product.subTitle}>Detalji proizvoda</h4>
              <div css={product.linkButton}>
                <LinkIcon />
                <a
                  href={`https://www.wienerberger.hr/services/productdetail?productID=${commercial.code}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <FormattedMessage id="template.product-page.link" />
                </a>
              </div>
            </div>
          )}
          {(isRoof || isRoofWindow) && skuAccessories && (
            <SystemSolutions
              skuAccessories={skuAccessories}
              title="Možda će vam trebati i:"
            />
          )}
        </section>

        <section css={product.layoutFlexItem}>
          <div css={product.titleFlex}>
            <h1 css={product.title}>{commercial.localizedName}</h1>

            <div css={product.brand}>
              <img
                alt={commercial.brand}
                src={resolveBrandIcon(commercial.brand)}
                css={product.photo}
              />
            </div>
          </div>

          <p css={product.skuNumber}>SKU: {commercial.SKUNumber}</p>

          <ProductImage
            productImage={productImage}
            className={product.mobileImageWrapper}
            images={[productImage, ...promoImages]}
          />

          {isRoof && <Guarantee className={product.guarantee} />}

          <div css={[product.desktopDescription, spacers.bottom.huge]}>
            {commercial.description && (
              <p
                dangerouslySetInnerHTML={createMarkup(commercial.description)}
              />
            )}
          </div>

          {isRoof && (
            <div css={product.remarkWrapper}>
              <p css={product.remark}>
                <FormattedMessage id="template.product-page.warning" />
              </p>
            </div>
          )}

          {isRoof && colorsObject && (
            <ProductColorAlternatives colors={colorsObject} />
          )}

          <div css={product.ctaWrapper}>
            <div css={[spacers.bottom.medium]}>
              <div css={product.ctaPrice}>
                <div>
                  <p css={product.suggestedPriceLabel}>
                    <FormattedMessage id="template.product-page.suggested-price" />
                  </p>
                  <p css={product.suggestedPrice}>
                    <span>
                      {formatPrice(sku?.price?.pricePerPiece)}{' '}
                      {sku?.price?.currency || 'XX'}
                    </span>{' '}
                    + <FormattedMessage id="template.product-page.vat" />
                  </p>
                </div>

                <p css={[product.suggestedPriceLabel, spacers.bottom.small]}>
                  <FormattedMessage id="template.product-page.price-without-vat" />
                </p>
                <p css={product.pricePerMetric}>
                  <FormattedMessage id="template.product-page.price-per-piece" />
                </p>
              </div>

              {Boolean(productPricePerM2) && !isNaN(productPricePerM2) && (
                <p css={[product.suggestedPriceLabel, spacers.top.small]}>
                  <FormattedMessage id="template.product-page.price-per-metric" />{' '}
                  m<sup>2</sup>: {formatPrice(productPricePerM2)}{' '}
                  {sku?.price?.currency || 'XX'} +{' '}
                  <FormattedMessage id="template.product-page.vat" />
                </p>
              )}
              <p css={[product.suggestedPriceLabel, spacers.top.small]}>
                <FormattedMessage id="template.product-page.total-price" />{' '}
                {formatPrice(totalPrice)} {sku?.price?.currency || 'XX'} +{' '}
                <FormattedMessage id="template.product-page.vat" />
              </p>
            </div>

            <div css={product.desktopWidth}>
              <div css={product.inputsFlex}>
                <div css={product.quantityWrapper}>
                  <ControlledInput
                    type="number"
                    name="quantity"
                    label={formatMessage({
                      id: 'template.product-page.quantity',
                    })}
                    size="small"
                    value={quantity}
                    onChange={changeQuantity}
                    className={product.quantityInput}
                  />
                  {quantityError && (
                    <p css={product.errorMsg}>
                      <FormattedMessage id="template.product-page.quantity-error" />
                    </p>
                  )}
                </div>
              </div>
              <div css={product.addToCartWrapper}>
                <button
                  type="submit"
                  css={product.ctaBtn}
                  disabled={quantityError}
                  onClick={handleAddToCart}
                >
                  <FormattedMessage id="template.product-page.offer-request" />
                </button>
                {commercial.dryFixM2Can && minPiecesPerMsq && (
                  <p css={[product.suggestedPriceLabel, spacers.top.small]}>
                    <FormattedMessage id="template.product-page.porotherm-extra" />
                  </p>
                )}
              </div>
            </div>
          </div>

          <div css={[product.mobileDescription, spacers.bottom.huge]}>
            {commercial.description && (
              <p
                dangerouslySetInnerHTML={createMarkup(commercial.description)}
              />
            )}
          </div>

          {sku && <TechnicalDetails commercial={commercial} sku={sku} />}
        </section>
      </div>

      <MobileDetailsAccordion commercial={commercial} sku={sku} />

      {commercial.youtubeLinks && (
        <VideoMaterials links={commercial.youtubeLinks} />
      )}

      {skuAccessories && !isRoof && !isRoofWindow && (
        <SystemSolutions skuAccessories={skuAccessories} />
      )}

      <LastViewedProducts />
      <ToastContainer
        position="bottom-center"
        autoClose={10000}
        hideProgressBar={false}
        newestOnTop
        rtl={false}
        pauseOnFocusLoss
        closeButton={false}
        style={{
          width: '70%',
        }}
        toastStyle={{
          borderRadius: '4px',
        }}
      />
    </Container>
  );

  function resolveBrandIcon(brand: string) {
    switch (brand) {
      case 'Tondach':
        return require('content/logo-tondach.svg');

      case 'Porotherm':
        return require('content/logo-porotherm.svg');

      case 'Terca':
        return require('content/logo-terca.svg');

      default:
        return null;
    }
  }

  function getSEOTitle() {
    switch (commercial.navigationCategories.code) {
      case 'Wall':
        return '';
      case 'Facade':
        return 'Terca ';
      case 'Roof':
        return 'Tondach ';
      case 'RoofWindows':
        return 'Tondach ';
      default:
        return '';
    }
  }

  function handleParseProduct(product: Product) {
    if ('language' in product) delete product.language;
    if ('intl' in product) delete product.intl;

    return product;
  }

  function handleAddToCart() {
    if (quantityError) return;
    const cartProduct: CartProduct = {
      product: handleParseProduct(pageContext),
      id: pageContext.id,
      imageSrc:
        commercial.assets.mainImg || commercial.assets.otherImgs[0].imgUrl,
      title: commercial.localizedName,
      quantity: {
        quantity,
        type: 'kom',
      },
      deliveryLocation: delivery,
      price: sku?.price?.pricePerPiece,
      dryFixM2Can: commercial.dryFixM2Can,
    };

    dispatch(CartThunks.addProduct(cartProduct));

    if (dryfixQuery && commercial.dryFixM2Can && minPiecesPerMsq) {
      const dryfixQuantity = Math.ceil(
        quantity / minPiecesPerMsq / commercial.dryFixM2Can,
      );
      const dryfixProduct: CartProduct = getCartModel(
        [
          {
            queryProduct: dryfixQuery,
            quantity: dryfixQuantity,
          },
        ],
        delivery,
        true,
      )[0];

      dispatch(CartThunks.addProduct(dryfixProduct));
    }

    toast.success(
      <SuccessToast
        title=" "
        text={formatMessage(
          { id: 'template.product-page.add-to-cart' },
          { item: commercial.localizedName },
        )}
      />,
    );
  }

  /* function handleDeliveryChange(value: SelectOption<DeliveryOption>) {
    setDelivery(value.value);
  } */

  function changeQuantity(value: number) {
    if (!value || value <= 0) {
      if (!quantityError) setQuantityError(true);
    } else {
      if (quantityError) setQuantityError(false);
    }
    setQuantity(value);
  }
};

export default ProductPage;
